import { lazy, type ComponentType } from 'react';

export const CHUNK_RELOAD_KEY = 'ss_chunk_reload_attempted';
export const CHUNK_RELOAD_WINDOW_MS = 10_000;
export const CHUNK_RELOAD_MESSAGE = 'A new version is available. Reloading';
let reloadRequested = false;

/** Share a short cooldown across page reloads without blocking later deploys. */
export function reloadOnce(): boolean {
  if (reloadRequested) return true;
  if (typeof window === 'undefined') return false;
  try {
    const now = Date.now();
    const previous = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
    const attemptedAt = Number(previous);
    if (previous !== null && Number.isFinite(attemptedAt) && now - attemptedAt < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // Without a persistent guard, an automatic reload could loop.
    return false;
  }
  reloadRequested = true;
  window.location.reload();
  return true;
}

export class ChunkImportError extends Error {
  constructor(public readonly reloading: boolean, cause: unknown) {
    super(reloading ? CHUNK_RELOAD_MESSAGE : 'Could not load this part of the app. Please reload and try again.', { cause });
    this.name = 'ChunkImportError';
  }
}

/** Shared by routes and the on-demand PDF engine. Render failures stay separate. */
export async function importWithReload<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (cause) {
    throw new ChunkImportError(reloadOnce(), cause);
  }
}

export function lazyWithReload<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await importWithReload(load);
    } catch (error) {
      // Keep Suspense mounted while the browser navigates to the new entry.
      if (error instanceof ChunkImportError && error.reloading) {
        return await new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

export function installPreloadRecovery(): () => void {
  const onPreloadError = () => {
    // Let the import reject so its caller can keep Suspense or PDF copy intact.
    reloadOnce();
  };
  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
